import {
  useNavigate,
  Outlet,
  useParams,
} from 'react-router-dom';
import { useMe } from '../contexts/MeContext';
import { useCoinAnimation } from '../hooks/useCoinAnimation';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { getNameBadge } from '../lib/utils';
import { SwordIcon, StoreIcon, UserIcon } from './icons';
import { CoinDisplay } from './CoinDisplay';
import { Button } from './ui';
import { SoundType } from '../lib/audio';

export function DashboardLayout() {
  const navigate = useNavigate();
  const { realmId } = useParams<{ realmId?: string }>();
  const { hero, heroCoins } = useMe();
  const coinAnimation = useCoinAnimation(heroCoins);
  const isTablet = useBreakpoint('tablet');

  const handleShopClick = () => {
    if (realmId) {
      navigate(`/realms/${realmId}/shoppe`);
    } else {
      navigate('/shoppe');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-deep-blue-900 pixel-pattern">
      {/* Fixed Navigation Container */}
      <div
        className="fixed top-0 left-0 right-0 z-50 bg-deep-blue-800/90 border-b-2 border-deep-blue-600 backdrop-blur-xs"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <nav className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              {/* Logo/Title */}
              <Button
                to="/dashboard"
                className="font-pixel text-xl text-neon-blue-400 hidden sm:block"
              >
                CHOREBOARD
              </Button>
              <Button
                variant="unstyled"
                to="/dashboard"
                className="font-pixel text-neon-blue-400 sm:hidden"
              >
                <SwordIcon className="w-6 h-6" />
              </Button>

              {/* Hero Info */}
              {hero?.displayName && (
                <div className="flex items-center">
                  <Button
                    to="/hero"
                    className="font-pixel text-sm bg-neon-blue-400/10 px-3 py-1 rounded-lg border border-neon-blue-400/20 text-neon-blue-300 hover:bg-neon-blue-400/20 hover:border-neon-blue-400/30 transition-colors"
                    data-testid="hero-name-badge"
                  >
                    {isTablet
                      ? hero.displayName
                      : getNameBadge(hero.displayName)[0]}
                  </Button>
                  <div
                    className="relative bg-yellow-400/10 px-3 py-1 rounded-lg border border-yellow-400/20 ml-3"
                    data-testid="hero-coins-display"
                  >
                    <CoinDisplay
                      coins={heroCoins}
                      iconClassName="w-3 h-3"
                      className="font-pixel text-sm"
                      compactOnSmall
                    />

                    {/* Coin Animation */}
                    {coinAnimation.show && (
                      <div
                        className={`absolute -bottom-4 right-0 pointer-events-none font-pixel text-xs animate-bounce ${
                          coinAnimation.isPositive
                            ? 'text-yellow-400'
                            : 'text-red-400'
                        }`}
                      >
                        {coinAnimation.isPositive
                          ? '+'
                          : '-'}
                        <CoinDisplay
                          coins={coinAnimation.value}
                          iconClassName="w-2 h-2"
                          className="inline-flex"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Actions */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                onClick={handleShopClick}
                sound={SoundType.Navigate}
                className="font-pixel text-sm bg-neon-blue-400/20 text-neon-blue-400 hover:bg-neon-blue-400/30 transition-colors border border-neon-blue-400/30 hover:border-neon-blue-400/50 rounded-lg px-3 py-2 flex items-center gap-2"
                title="Shoppe"
                data-testid="shoppe-nav-button"
              >
                <StoreIcon className="w-4 h-4" />
                <span className="hidden sm:inline">
                  Shoppe
                </span>
              </Button>
              <Button
                to="/hero"
                className="font-pixel text-sm bg-neon-blue-400/20 text-neon-blue-400 hover:bg-neon-blue-400/30 transition-colors border border-neon-blue-400/30 hover:border-neon-blue-400/50 rounded-lg px-3 py-2 flex items-center gap-2"
                title="Hero"
                data-testid="hero-nav-link"
              >
                <UserIcon className="w-4 h-4" />
                <span className="hidden sm:inline">
                  Hero
                </span>
              </Button>
            </div>
          </div>
        </nav>
      </div>

      {/* Scrollable Main Content */}
      <main
        className="flex-1 overflow-y-auto px-4 sm:px-6"
        style={{
          paddingTop:
            'calc(4.5rem + env(safe-area-inset-top))',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <Outlet />
      </main>
    </div>
  );
}
